/**
 * One outreach list — /crm/outreach/:id (docs/crm-outreach-lists-plan.md §B7).
 *
 * Three stacked cards, in the order a manager asks the questions:
 *   About     what the list is for — study, where the people came from (the source search and
 *             its steps, in words), the first message and what happened to it (texts sent /
 *             waiting / stopped, and why), who was left off at launch — plus, while the list is
 *             still being set up, "Setting up — 40 of 212 ready" on a 3-second poll. A plain
 *             sentence from the server explains a setup that went wrong.
 *   Recruiters  per person: open · replies waiting (and the oldest wait) · tried today ·
 *             interested · moved ahead · last outcome — and an "Out today" badge with "Share
 *             their untouched out", because a recruiter off sick is the moment a list stalls.
 *   People    the members, 50 a page, filtered by recruiter / state / outcome, each row's state
 *             in the list's own words (outreachLogic.bucketLabel) and its pipeline stage in the
 *             usual stage colors.
 *
 * Manager actions (CRM admins only — the server re-checks every one): rename, move or remove
 * selected people, share out the people nobody has called yet, stop unsent texts, re-run the
 * agent's opening texts after a crashed setup, and close the list. Every confirm goes through the
 * CRM's promise dialogs (useConfirm / usePromptDialog) or a small Modal where the question needs
 * a choice (a recruiter, a checkbox) — never window.confirm, which freezes the tab mid-call.
 *
 * "Work through" is for the recruiters ON the list; it starts the Next-up session scoped to this
 * list (useListWorkThrough) and, when there is nobody to call right now, says when the next tries
 * start instead of entering an empty session.
 *
 * Phones (useIsPhone, below `sm`): the Recruiters and People tables — seven and nine columns that
 * only a sideways scroll could reach — render as card lists instead, one card per recruiter or
 * person carrying the same facts under small labels, the same links, and the same selection
 * checkboxes for the manager actions. The People filters become one swipeable strip. The desktop
 * tables are untouched; the two renderings share the row facts through the helpers at the bottom.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowRightLeft,
  Bot,
  ChevronLeft,
  ChevronRight,
  ListChecks,
  Loader2,
  MessageSquareText,
  Pencil,
  Send,
  Share2,
  Trash2,
  Users,
  XCircle,
  Zap,
} from 'lucide-react';
import { PageHeader, RefreshButton } from '../components';
import { FilterBar, FilterSelect } from '../components/filters';
import { Alert, Badge, btnDanger, btnGhost, btnPrimary, useConfirm, usePromptDialog } from '../components/ui';
import { RowLink } from '../components/shared';
import LifecycleBadge from '../components/LifecycleBadge';
import Modal from '../components/forms/Modal';
import { outTodaySuffix } from '../components/OwnerSelect';
import EmptyState from '../../ui/EmptyState';
import { notifyStatsChanged, relTime, useIsPhone, useTeam } from '../lib';
import {
  cancelOutreachTexts,
  getOutreachList,
  getOutreachMembers,
  isUnavailable,
  outreachErrorMessage,
  reassignOutreachMembers,
  removeOutreachMembers,
  sendOutreachAgentTexts,
  shareOutUntouched,
  updateOutreachList,
} from './api';
import type { OutreachListDetail as ListDetail, OutreachMember, OutreachRecruiterRow } from './types';
import { FLAG_LABELS, OUTCOME_LABELS, bucketLabel, shortWhen, splitRoundRobin } from './outreachLogic';
import {
  LIST_STATUS_LABELS,
  LIST_STATUS_TONE,
  MEMBERS_PAGE_SIZE,
  MEMBER_STATE_FILTERS,
  MESSAGE_MODE_LABELS,
  cancelledReasonsLines,
  defaultShareRecipients,
  excludedLine,
  isUuid,
  launchProgressLine,
  listProgress,
  memberTextLabel,
  normalizeListDetail,
  normalizeMember,
  pageRangeLabel,
  sharedNotice,
  textsLine,
  untouchedMemberIds,
} from './outreachSurfaces';
import { ProgressBar } from './OutreachListsPage';
import { useListWorkThrough } from './OutreachTodaySection';

const cardCls = 'rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800';
const cardHeadCls = 'flex flex-wrap items-center gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-700';
const cardTitleCls = 'text-sm font-semibold text-gray-900 dark:text-gray-100';
const thCls = 'px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400';
const tdCls = 'px-3 py-2.5 text-sm text-gray-700 dark:text-gray-200';
const tdNum = 'px-3 py-2.5 text-right text-sm tabular-nums text-gray-700 dark:text-gray-200';
const labelCls = 'text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500';
/** Appended to a kit button: a full-width centered row below sm, the button's own shape from sm up. */
const phoneRowBtn = 'max-sm:w-full max-sm:justify-center';

/** The largest batch one reassign call carries (a 2,000-person list shares out in ten). */
const REASSIGN_CHUNK = 200;

type Dialog =
  | { kind: 'move' }
  | { kind: 'share' }
  | { kind: 'shareFrom'; userId: string; name: string }
  | { kind: 'close' };

const dateOnly = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

/** "Out today, back Mon" for the recruiters table (the same words as every owner picker). */
const outLabel = (r: OutreachRecruiterRow): string => outTodaySuffix(r).replace(/^ — /, '');

/**
 * "Next try" is when a recruiter can next CALL them. While they wait on the list's own first
 * message, waiting_until is that message's send time (the First text column already shows it),
 * not a call time — so nothing is shown then. Shared by the table row and the phone card.
 */
const memberNextTry = (m: OutreachMember): string | null => {
  const waitingOnFirstMessage = m.bucket === 'waiting_text' || m.bucket === 'waiting_after_text' || m.bucket === 'waiting_agent';
  return m.state === 'open'
    ? m.next_attempt_at || (waitingOnFirstMessage ? null : m.waiting_until)
    : null;
};

/** The member's flags in words, unknown codes dropped. Shared by the table row and the phone card. */
const memberFlagLabels = (m: OutreachMember): string[] => m.flags.map((f) => FLAG_LABELS[f]).filter(Boolean);

/**
 * One labelled fact on a phone card (a <dt>/<dd> pair inside the card's <dl> grid): the table
 * column's heading as a micro-label over its value. `wide` spans both grid columns for the
 * longer sentences ("Where they are", "First text").
 */
function Fact({
  label,
  children,
  title,
  wide = false,
  strong = false,
  muted = false,
  size = 'sm',
}: {
  label: string;
  children: ReactNode;
  title?: string;
  wide?: boolean;
  strong?: boolean;
  muted?: boolean;
  size?: 'sm' | 'xs';
}) {
  return (
    <div className={`min-w-0 ${wide ? 'col-span-2' : ''}`} title={title}>
      <dt className={labelCls}>{label}</dt>
      <dd
        className={`mt-0.5 ${size === 'xs' ? 'text-xs' : 'text-sm tabular-nums'} ${
          strong
            ? 'font-semibold text-gray-900 dark:text-gray-100'
            : muted
              ? 'text-gray-500 dark:text-gray-400'
              : 'text-gray-700 dark:text-gray-200'
        }`}
      >
        {children}
      </dd>
    </div>
  );
}

export default function OutreachListDetail() {
  const { id: rawId } = useParams();
  const listId = isUuid(rawId) ? rawId : null;
  const confirm = useConfirm();
  const prompt = usePromptDialog();
  const work = useListWorkThrough();
  const { team, byId: teamById } = useTeam();
  // Card lists in place of the two wide tables below sm (see the header comment).
  const isPhone = useIsPhone();

  const [list, setList] = useState<ListDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [missing, setMissing] = useState<'not_found' | 'unavailable' | null>(listId ? null : 'not_found');

  // People table
  const [assignee, setAssignee] = useState('');
  const [stateFilter, setStateFilter] = useState<'' | 'open' | 'done' | 'removed'>('open');
  const [outcome, setOutcome] = useState('');
  const [offset, setOffset] = useState(0);
  const [members, setMembers] = useState<OutreachMember[]>([]);
  const [total, setTotal] = useState(0);
  const [membersLoading, setMembersLoading] = useState(true);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [membersToken, setMembersToken] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Actions
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog | null>(null);

  const loadList = useCallback(async (silent = false) => {
    if (!listId) return;
    if (!silent) setLoading(true);
    try {
      const l = await getOutreachList(listId);
      if (!l) {
        setMissing('not_found');
      } else {
        setList(normalizeListDetail(l));
        setMissing(null);
        setLoadError(null);
      }
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      // The 3-second setup poll and post-action refreshes are silent: a dropped one keeps the
      // page as it is (the next tick or the Refresh button catches up). A real 404 still wins —
      // the list is gone.
      if (silent && status !== 404) return;
      if (status === 404) setMissing('not_found');
      else if (isUnavailable(e)) setMissing('unavailable');
      else setLoadError(outreachErrorMessage(e, "Couldn't load this outreach list. Please try again."));
    } finally {
      setLoading(false);
    }
  }, [listId]);

  useEffect(() => { loadList(); }, [loadList]);

  // "Setting up — 40 of 212 ready" moves on its own: a 3-second poll for exactly as long as the
  // list is launching. When it flips to active, the people table reloads once.
  const launching = list?.status === 'launching';
  useEffect(() => {
    if (!launching) return undefined;
    const t = window.setInterval(() => loadList(true), 3_000);
    return () => {
      window.clearInterval(t);
      setMembersToken((n) => n + 1);
    };
  }, [launching, loadList]);

  useEffect(() => {
    if (!listId) return undefined;
    let alive = true;
    setMembersLoading(true);
    getOutreachMembers(listId, {
      ...(assignee ? { assignee } : {}),
      ...(stateFilter ? { state: stateFilter } : {}),
      ...(outcome ? { outcome } : {}),
      offset,
      limit: MEMBERS_PAGE_SIZE,
    })
      .then((page) => {
        if (!alive) return;
        setMembers(page.members.map(normalizeMember));
        setTotal(page.total);
        setMembersError(null);
      })
      .catch((e) => {
        if (!alive) return;
        setMembers([]);
        setTotal(0);
        setMembersError(outreachErrorMessage(e, "Couldn't load the people on this list. Please try again."));
      })
      .finally(() => { if (alive) setMembersLoading(false); });
    return () => { alive = false; };
  }, [listId, assignee, stateFilter, outcome, offset, membersToken]);

  // A new filter or page is a new set of rows — a selection must never carry over onto people
  // the manager can no longer see.
  useEffect(() => { setSelected(new Set()); }, [assignee, stateFilter, outcome, offset, membersToken]);

  const refreshAll = useCallback(() => {
    loadList(true);
    setMembersToken((n) => n + 1);
  }, [loadList]);

  /** Run one manager action: busy flag, plain-sentence error, refresh everything after. */
  const run = async (key: string, fn: () => Promise<string | null>) => {
    if (busy) return;
    setBusy(key);
    setActionError(null);
    setNotice(null);
    try {
      const msg = await fn();
      if (msg) setNotice(msg);
      notifyStatsChanged();
      refreshAll();
    } catch (e) {
      setActionError(outreachErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const isAdmin = !!list?.viewer.is_admin;
  const isOpenList = list?.status === 'active' || list?.status === 'launching';
  const recruiterName = (userId: string) =>
    list?.recruiters.find((r) => r.user_id === userId)?.name || teamById.get(userId)?.name || 'a teammate';

  // Team members for the pickers: the list's own recruiters first, then everyone else.
  const pickable = useMemo(() => {
    const onList = new Set((list?.recruiters || []).filter((r) => r.active).map((r) => r.user_id));
    return [...team].sort((a, b) => Number(onList.has(b.id)) - Number(onList.has(a.id)) || a.name.localeCompare(b.name));
  }, [team, list?.recruiters]);

  // ------------------------------------------------------------------ actions

  const rename = async () => {
    if (!list || !listId) return;
    const next = await prompt({
      title: 'Rename this list',
      label: 'List name',
      initialValue: list.name,
      confirmLabel: 'Save',
    });
    const name = next?.trim();
    if (!name || name === list.name) return;
    if (name.length > 120) {
      setActionError('Keep the name to 120 characters or fewer.');
      return;
    }
    run('rename', async () => {
      await updateOutreachList(listId, { name });
      return 'Renamed.';
    });
  };

  const removeSelected = async () => {
    if (!listId || selected.size === 0) return;
    const n = selected.size;
    const ok = await confirm({
      title: `Take ${n === 1 ? 'this person' : `these ${n} people`} off the list?`,
      message:
        'They go back to the normal lead lists and keep their current stage. Any list text that has not gone out to them is stopped.',
      confirmLabel: n === 1 ? 'Take off the list' : `Take ${n} off the list`,
      danger: true,
    });
    if (!ok) return;
    const ids = Array.from(selected);
    run('remove', async () => {
      const res = await removeOutreachMembers(listId, ids);
      const removed = typeof res?.removed === 'number' ? res.removed : ids.length;
      return `Took ${removed === 1 ? '1 person' : `${removed.toLocaleString()} people`} off the list.`;
    });
  };

  const moveSelected = (toUserId: string) => {
    if (!listId || selected.size === 0 || !toUserId) return;
    const ids = Array.from(selected);
    setDialog(null);
    run('move', async () => {
      let moved = 0;
      for (let i = 0; i < ids.length; i += REASSIGN_CHUNK) {
        const res = await reassignOutreachMembers(listId, ids.slice(i, i + REASSIGN_CHUNK), toUserId);
        moved += typeof res?.moved === 'number' ? res.moved : Math.min(REASSIGN_CHUNK, ids.length - i);
      }
      return sharedNotice(moved, [recruiterName(toUserId)]);
    });
  };

  const shareOut = (userIds: string[]) => {
    if (!listId || userIds.length === 0) return;
    setDialog(null);
    run('share', async () => {
      const res = await shareOutUntouched(listId, userIds);
      const moved = typeof res?.moved === 'number' ? res.moved : 0;
      return sharedNotice(moved, userIds.map(recruiterName));
    });
  };

  /**
   * "Share their untouched out" for one recruiter who is out. The share-out route shares the whole
   * list's untouched people, so this reads that recruiter's open people, keeps the ones nobody has
   * called yet, and hands them round-robin to the chosen recruiters through the reassign route —
   * both of which the server re-authorizes and re-checks row by row.
   */
  const shareFrom = (fromUserId: string, toUserIds: string[]) => {
    if (!listId || toUserIds.length === 0) return;
    setDialog(null);
    run('shareFrom', async () => {
      const ids: string[] = [];
      let off = 0;
      for (let guard = 0; guard < 100; guard += 1) {
        const page = await getOutreachMembers(listId, { assignee: fromUserId, state: 'open', bucket: 'untouched', offset: off, limit: REASSIGN_CHUNK });
        ids.push(...untouchedMemberIds(page.members));
        off += page.members.length;
        if (page.members.length === 0 || off >= page.total) break;
      }
      const split = splitRoundRobin(ids, toUserIds);
      let moved = 0;
      for (const to of toUserIds) {
        const mine = split[to] || [];
        for (let i = 0; i < mine.length; i += REASSIGN_CHUNK) {
          const chunk = mine.slice(i, i + REASSIGN_CHUNK);
          const res = await reassignOutreachMembers(listId, chunk, to);
          moved += typeof res?.moved === 'number' ? res.moved : chunk.length;
        }
      }
      return sharedNotice(moved, toUserIds.map(recruiterName));
    });
  };

  const cancelTexts = async () => {
    if (!listId || !list) return;
    const waiting = list.texts.scheduled;
    const ok = await confirm({
      title: 'Stop the texts that haven’t gone out yet?',
      message: `${waiting.toLocaleString()} ${waiting === 1 ? 'text is' : 'texts are'} still waiting to send. Stopping them can’t be undone — everyone stays on the list for calls.`,
      confirmLabel: 'Stop unsent texts',
      danger: true,
    });
    if (!ok) return;
    run('cancelTexts', async () => {
      const res = await cancelOutreachTexts(listId);
      const n = typeof res?.cancelled === 'number' ? res.cancelled : 0;
      return n ? `Stopped ${n.toLocaleString()} unsent ${n === 1 ? 'text' : 'texts'}.` : 'There were no unsent texts left to stop.';
    });
  };

  const sendAgentTexts = async () => {
    if (!listId) return;
    const ok = await confirm({
      title: 'Send the agent’s opening texts now?',
      message:
        'Setting up this list stopped before the texting agent sent its first messages. This sends them now, at the list’s pace, to the people who haven’t had one.',
      confirmLabel: 'Send agent texts',
    });
    if (!ok) return;
    run('agentTexts', async () => {
      const res = await sendOutreachAgentTexts(listId);
      const n = typeof res?.scheduled === 'number' ? res.scheduled : 0;
      return n ? `The agent will text ${n.toLocaleString()} ${n === 1 ? 'person' : 'people'}.` : 'Nobody was left for the agent to text.';
    });
  };

  const closeList = (archiveUnreached: boolean) => {
    if (!listId) return;
    setDialog(null);
    run('close', async () => {
      await updateOutreachList(listId, { status: 'completed', archive_unreached: archiveUnreached });
      return 'List closed.';
    });
  };

  // ------------------------------------------------------------------ render

  if (missing) {
    return (
      <div className="space-y-4">
        <BackLink />
        <EmptyState
          variant="card"
          icon={ListChecks}
          title={missing === 'not_found' ? "We couldn't find that outreach list" : "Outreach lists aren't available right now"}
          hint={
            missing === 'not_found'
              ? 'It may have been removed, or it isn’t one of the lists you work on.'
              : 'This part of the CRM is still being switched on, or couldn’t be reached. Try again in a moment.'
          }
          action={missing === 'unavailable' ? <button type="button" onClick={() => loadList()} className={btnGhost}>Try again</button> : undefined}
        />
      </div>
    );
  }

  if (!list) {
    return (
      <div className="space-y-4">
        <BackLink />
        {loadError ? (
          <Alert tone="error" icon={false} actions={<button type="button" onClick={() => loadList()} className="font-medium underline hover:no-underline">Retry</button>}>
            {loadError}
          </Alert>
        ) : (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" />
          </div>
        )}
      </div>
    );
  }

  const prog = listProgress(list.counts);
  const texts = textsLine(list.texts);
  const stopped = cancelledReasonsLines(list.texts.cancelled_reasons);
  const excluded = excludedLine(list.launch.excluded);
  const steps = (list.source.step_summaries || []).filter(Boolean);
  const searchHref = list.source.search_id
    ? `/crm/search/${list.source.search_id}`
    : list.source.search_run_id
      ? `/crm/search/runs/${list.source.search_run_id}`
      : null;
  // Only open people can be moved or taken off, so "select everyone on this page" means them.
  const pageIds = members.filter((m) => m.state === 'open').map((m) => m.id);
  const allOnPage = pageIds.length > 0 && pageIds.every((mid) => selected.has(mid));
  const canWork = !work.sessionActive && list.viewer.is_recruiter && list.status === 'active';

  const launchFacts = [
    `${list.launch.total.toLocaleString()} ${list.launch.total === 1 ? 'person' : 'people'} added`,
    list.launch.reopened ? `${list.launch.reopened.toLocaleString()} reopened for this study` : null,
    list.launch.owner_changes ? `${list.launch.owner_changes.toLocaleString()} moved to a new recruiter` : null,
    list.launch.stopped_automations ? `${list.launch.stopped_automations.toLocaleString()} old automated messages stopped` : null,
  ].filter(Boolean) as string[];

  return (
    <div className="space-y-4">
      <BackLink />
      <PageHeader
        icon={ListChecks}
        title={
          <span className="flex min-w-0 items-center gap-2">
            <span className="min-w-0 truncate">{list.name}</span>
            {isAdmin && (
              <button
                type="button"
                onClick={rename}
                disabled={busy !== null}
                aria-label="Rename this list"
                title="Rename this list"
                className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:hover:bg-gray-700 dark:hover:text-primary-400"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>{list.study.title}</span>
            <Badge tone={LIST_STATUS_TONE[list.status]} size="sm" dot>{LIST_STATUS_LABELS[list.status]}</Badge>
            <span className="text-gray-400 dark:text-gray-500">
              Started by {list.created_by?.name || 'someone'}{list.created_at ? ` on ${dateOnly(list.launched_at || list.created_at)}` : ''}
              {list.status === 'completed' && list.completed_at ? ` · closed ${dateOnly(list.completed_at)}` : ''}
            </span>
          </span>
        }
        actions={
          <>
            {canWork && (
              <button type="button" onClick={() => work.start(list.id)} disabled={work.starting} className={btnPrimary}>
                {work.busyFor === list.id ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Zap className="h-4 w-4" aria-hidden="true" />}
                Work through
              </button>
            )}
            <RefreshButton onClick={refreshAll} loading={loading} label="Refresh this list" />
          </>
        }
      />

      {work.emptyLine && <Alert tone="info" size="sm">{work.emptyLine}</Alert>}
      {work.errorLine && <Alert tone="error" size="sm">{work.errorLine}</Alert>}
      {notice && <Alert tone="success" size="sm" onDismiss={() => setNotice(null)}>{notice}</Alert>}
      {actionError && <Alert tone="error" size="sm" onDismiss={() => setActionError(null)}>{actionError}</Alert>}

      {/* ---------------------------------------------------------------- About */}
      <section className={cardCls}>
        <div className={cardHeadCls}>
          <MessageSquareText className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          <h2 className={cardTitleCls}>About this list</h2>
          {list.status !== 'launching' && <div className="ml-auto w-full max-w-xs sm:w-auto"><ProgressBar {...prog} /></div>}
        </div>
        <div className="space-y-4 p-4">
          {list.status === 'launching' && (
            <div className="space-y-1.5" role="status" aria-live="polite">
              <p className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-100">
                <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
                {launchProgressLine(list.launch)}
              </p>
              <ProgressBar
                finished={list.launch.prepared}
                total={list.launch.total}
                pct={list.launch.total ? Math.round((Math.min(list.launch.prepared, list.launch.total) / list.launch.total) * 100) : 0}
              />
            </div>
          )}

          {list.launch_error && (
            <Alert
              tone="warning"
              size="sm"
              actions={isAdmin && list.agent_texts_pending ? (
                <button type="button" onClick={sendAgentTexts} disabled={busy !== null} className={btnGhost}>
                  <Bot className="h-4 w-4" aria-hidden="true" /> Send agent texts
                </button>
              ) : undefined}
            >
              {list.launch_error}
            </Alert>
          )}

          {/* min-w-0 on both columns: a grid column's automatic minimum is its content's
              min-content width, and the first message's unbreakable URL made that wider than
              a phone, pushing the whole card past the screen edge (the criteria list was the
              visible casualty). With the floor removed the column stays inside the card and
              break-words lets the URL wrap inside the quote. */}
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="min-w-0 space-y-1">
              <p className={labelCls}>Who's on it</p>
              <p className="text-sm text-gray-700 dark:text-gray-200">
                {searchHref ? (
                  <>From the search <Link to={searchHref} className="font-medium text-primary-700 hover:underline dark:text-primary-400">{list.source.search_name || 'Untitled search'}</Link></>
                ) : (
                  'From a search'
                )}
              </p>
              {steps.length > 0 && (
                <ol className="list-decimal space-y-0.5 pl-5 text-xs text-gray-600 dark:text-gray-300">
                  {steps.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              )}
              <p className="pt-1 text-xs text-gray-500 dark:text-gray-400">{launchFacts.join(' · ')}</p>
              {excluded && <p className="text-xs text-gray-500 dark:text-gray-400">Left off: {excluded}</p>}
            </div>

            <div className="min-w-0 space-y-1">
              <p className={labelCls}>First message</p>
              <p className="text-sm text-gray-700 dark:text-gray-200">{MESSAGE_MODE_LABELS[list.message_mode]}</p>
              {list.message_mode === 'text' && list.message_body && (
                <blockquote className="whitespace-pre-wrap break-words rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
                  {list.message_body}
                </blockquote>
              )}
              {texts && <p className="text-xs text-gray-600 dark:text-gray-300">Texts: {texts}</p>}
              {stopped.length > 0 && (
                <ul className="space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                  {stopped.map((r) => <li key={r.label}>{r.count.toLocaleString()} stopped — {r.label.charAt(0).toLowerCase() + r.label.slice(1)}</li>)}
                </ul>
              )}
              <p className="pt-1 text-xs text-gray-500 dark:text-gray-400">
                Up to {list.settings.max_attempts} {list.settings.max_attempts === 1 ? 'try' : 'tries'} each, about {list.settings.retry_after_hours} hours apart
                {list.message_mode === 'text' ? ` · calls start ${list.settings.call_after_text_hours} hours after the text` : ''}
                {list.message_mode === 'agent' ? ` · recruiters are served these people after ${list.settings.agent_call_delay_days} days if the agent hasn't reached them` : ''}
              </p>
            </div>
          </div>

          {isAdmin && isOpenList && (
            // Phones: each manager action is a full-width row (a long label no
            // longer folds into two lines inside a narrow button); from sm up
            // they sit in one wrapping row as before.
            <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3 dark:border-gray-700">
              <button type="button" onClick={() => setDialog({ kind: 'share' })} disabled={busy !== null} className={`${btnGhost} ${phoneRowBtn}`}>
                <Share2 className="h-4 w-4 flex-shrink-0" aria-hidden="true" /> Share out the people nobody has called yet
              </button>
              {list.texts.scheduled > 0 && (
                <button type="button" onClick={cancelTexts} disabled={busy !== null} className={`${btnGhost} ${phoneRowBtn}`}>
                  <XCircle className="h-4 w-4 flex-shrink-0" aria-hidden="true" /> Stop unsent texts
                </button>
              )}
              {list.agent_texts_pending && !list.launch_error && (
                <button type="button" onClick={sendAgentTexts} disabled={busy !== null} className={`${btnGhost} ${phoneRowBtn}`}>
                  <Bot className="h-4 w-4 flex-shrink-0" aria-hidden="true" /> Send agent texts
                </button>
              )}
              {list.status === 'active' && (
                <button type="button" onClick={() => setDialog({ kind: 'close' })} disabled={busy !== null} className={`${btnGhost} ${phoneRowBtn} sm:ml-auto`}>
                  Close list
                </button>
              )}
              {busy && <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" aria-label="Working" />}
            </div>
          )}
        </div>
      </section>

      {/* ----------------------------------------------------------- Recruiters */}
      {list.recruiters.length > 0 && (
        <section className={cardCls}>
          <div className={cardHeadCls}>
            <Users className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
            <h2 className={cardTitleCls}>Recruiters</h2>
          </div>
          {isPhone ? (
            // Phone: one card per recruiter — the same seven facts as the table's columns, as
            // a labelled stat grid, with the share action as a full-width row beneath.
            <ul className="divide-y divide-gray-100 dark:divide-gray-700/60">
              {list.recruiters.map((r) => (
                <li key={r.user_id} className={`px-4 py-3 ${r.active ? '' : 'opacity-60'}`}>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{r.name}</span>
                    {r.out_today && <Badge tone="amber" size="sm">{outLabel(r) || 'Out today'}</Badge>}
                    {!r.active && <span className="text-xs text-gray-400">No longer on this list</span>}
                  </div>
                  <dl className="mt-2 grid grid-cols-3 gap-x-3 gap-y-2">
                    <Fact label="Open" title="People still open on the list">{r.open.toLocaleString()}</Fact>
                    <Fact label="Replies waiting" title="Replies waiting on us — and how long the oldest has waited" strong={r.replied_waiting > 0}>
                      {r.replied_waiting.toLocaleString()}
                      {r.replied_waiting > 0 && r.oldest_reply_at && (
                        <span className="block text-xs font-normal text-gray-500 dark:text-gray-400">oldest {relTime(r.oldest_reply_at)}</span>
                      )}
                    </Fact>
                    <Fact label="Tried today">{r.tried_today.toLocaleString()}</Fact>
                    <Fact label="Interested">{r.reached.toLocaleString()}</Fact>
                    <Fact label="Moved ahead" title="Went on to be scheduled, screened or enrolled">{r.advanced.toLocaleString()}</Fact>
                    <Fact label="Last outcome" muted>{r.last_outcome_at ? relTime(r.last_outcome_at) : 'Not yet'}</Fact>
                  </dl>
                  {isAdmin && r.out_today && r.open > 0 && isOpenList && (
                    <button
                      type="button"
                      onClick={() => setDialog({ kind: 'shareFrom', userId: r.user_id, name: r.name })}
                      disabled={busy !== null}
                      className={`${btnGhost} mt-3 w-full justify-center`}
                    >
                      <Share2 className="h-4 w-4" aria-hidden="true" /> Share their untouched out
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className={thCls}>Recruiter</th>
                  <th className={`${thCls} text-right`} title="People still open on the list">Open</th>
                  <th className={`${thCls} text-right`} title="Replies waiting on us — and how long the oldest has waited">Replies waiting</th>
                  <th className={`${thCls} text-right`}>Tried today</th>
                  <th className={`${thCls} text-right`}>Interested</th>
                  <th className={`${thCls} text-right`} title="Went on to be scheduled, screened or enrolled">Moved ahead</th>
                  <th className={thCls}>Last outcome</th>
                  {isAdmin && <th className={thCls}><span className="sr-only">Actions</span></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                {list.recruiters.map((r) => (
                  <tr key={r.user_id} className={r.active ? '' : 'opacity-60'}>
                    <td className={tdCls}>
                      <span className="flex flex-wrap items-center gap-1.5">
                        <span className="font-medium text-gray-900 dark:text-gray-100">{r.name}</span>
                        {r.out_today && <Badge tone="amber" size="sm">{outLabel(r) || 'Out today'}</Badge>}
                        {!r.active && <span className="text-xs text-gray-400">No longer on this list</span>}
                      </span>
                    </td>
                    <td className={tdNum}>{r.open.toLocaleString()}</td>
                    <td className={`${tdNum} ${r.replied_waiting > 0 ? 'font-semibold text-gray-900 dark:text-gray-100' : ''}`}>
                      {r.replied_waiting.toLocaleString()}
                      {r.replied_waiting > 0 && r.oldest_reply_at && (
                        <span className="block text-xs font-normal text-gray-500 dark:text-gray-400">oldest {relTime(r.oldest_reply_at)}</span>
                      )}
                    </td>
                    <td className={tdNum}>{r.tried_today.toLocaleString()}</td>
                    <td className={tdNum}>{r.reached.toLocaleString()}</td>
                    <td className={tdNum}>{r.advanced.toLocaleString()}</td>
                    <td className={`${tdCls} whitespace-nowrap text-gray-500 dark:text-gray-400`}>
                      {r.last_outcome_at ? relTime(r.last_outcome_at) : 'Not yet'}
                    </td>
                    {isAdmin && (
                      <td className="px-3 py-2.5 text-right">
                        {r.out_today && r.open > 0 && isOpenList && (
                          <button
                            type="button"
                            onClick={() => setDialog({ kind: 'shareFrom', userId: r.user_id, name: r.name })}
                            disabled={busy !== null}
                            className="inline-flex min-h-[44px] items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                          >
                            <Share2 className="h-3.5 w-3.5" aria-hidden="true" /> Share their untouched out
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </section>
      )}

      {/* --------------------------------------------------------------- People */}
      <section className={cardCls}>
        <div className={cardHeadCls}>
          <ListChecks className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          <h2 className={cardTitleCls}>People</h2>
          <span className="text-sm text-gray-500 dark:text-gray-400">({total.toLocaleString()})</span>
        </div>
        <div className="space-y-3 p-4">
          <FilterBar mobileScroll>
            <FilterSelect
              auto
              label="Recruiter"
              value={assignee}
              onChange={(v) => { setAssignee(v); setOffset(0); }}
              options={[{ value: '', label: 'Every recruiter' }, ...list.recruiters.map((r) => ({ value: r.user_id, label: r.name }))]}
            />
            <FilterSelect
              auto
              label="State"
              value={stateFilter}
              onChange={(v) => { setStateFilter(v as typeof stateFilter); setOffset(0); }}
              options={MEMBER_STATE_FILTERS}
            />
            <FilterSelect
              auto
              label="Outcome"
              value={outcome}
              onChange={(v) => {
                setOutcome(v);
                // Most outcomes finish a person, so "Still open" + an outcome would read as empty.
                if (v && stateFilter === 'open') setStateFilter('');
                setOffset(0);
              }}
              options={[
                { value: '', label: 'Any outcome' },
                ...Object.entries(OUTCOME_LABELS).map(([value, label]) => ({ value, label })),
              ]}
            />
          </FilterBar>

          {isAdmin && isOpenList && selected.size > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2 dark:border-primary-900/60 dark:bg-primary-900/20">
              <span className="text-sm font-medium text-primary-800 dark:text-primary-300">{selected.size.toLocaleString()} selected</span>
              <button type="button" onClick={() => setDialog({ kind: 'move' })} disabled={busy !== null} className={btnGhost}>
                <ArrowRightLeft className="h-4 w-4" aria-hidden="true" /> Move selected to…
              </button>
              <button type="button" onClick={removeSelected} disabled={busy !== null} className={btnGhost}>
                <Trash2 className="h-4 w-4" aria-hidden="true" /> Remove selected
              </button>
              <button type="button" onClick={() => setSelected(new Set())} className="ml-auto text-xs font-medium text-gray-600 hover:underline dark:text-gray-300">
                Clear selection
              </button>
            </div>
          )}

          {membersError && (
            <Alert tone="error" size="sm" icon={false} actions={<button type="button" onClick={() => setMembersToken((n) => n + 1)} className="font-medium underline hover:no-underline">Retry</button>}>
              {membersError}
            </Alert>
          )}

          {isPhone ? (
            // Phone: one card per person. The same nine facts as the table's columns (name and
            // stage on the first line, the rest under small labels), the same lead link, and the
            // same selection checkbox for the manager actions — "select everyone on this page"
            // sits in the list's header row where the table's header checkbox was.
            <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
              {isAdmin && isOpenList && pageIds.length > 0 && (
                <label className="flex min-h-[44px] cursor-pointer items-center gap-2.5 border-b border-gray-200 bg-gray-50 px-3 text-xs font-medium text-gray-600 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={allOnPage}
                    onChange={() => setSelected(allOnPage ? new Set() : new Set(pageIds))}
                    aria-label="Select everyone on this page"
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                  />
                  Select everyone on this page
                </label>
              )}
              {membersLoading && members.length === 0 ? (
                <div className="px-3 py-10 text-center">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
                </div>
              ) : members.length === 0 ? (
                <p className="px-3 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                  Nobody on this list matches these filters.
                </p>
              ) : (
                <ul className={`divide-y divide-gray-100 dark:divide-gray-700/60 ${membersLoading ? 'opacity-60' : ''}`}>
                  {members.map((m) => {
                    const next = memberNextTry(m);
                    const flags = memberFlagLabels(m);
                    return (
                      <li key={m.id} className="px-3 py-3">
                        <div className="flex items-start gap-2.5">
                          {isAdmin && isOpenList && (
                            <span className="flex h-5 w-4 flex-shrink-0 items-center">
                              {m.state === 'open' && (
                                <input
                                  type="checkbox"
                                  checked={selected.has(m.id)}
                                  onChange={() => setSelected((prev) => {
                                    const n = new Set(prev);
                                    if (n.has(m.id)) n.delete(m.id); else n.add(m.id);
                                    return n;
                                  })}
                                  aria-label={`Select ${m.contact.name}`}
                                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                                />
                              )}
                            </span>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <RowLink
                                to={`/crm/contacts/${m.contact.id}`}
                                title={m.contact.name}
                                className="min-w-0 truncate text-sm font-medium text-gray-900 hover:text-primary-700 dark:text-gray-100 dark:hover:text-primary-400"
                              >
                                {m.contact.name || 'Unnamed lead'}
                              </RowLink>
                              <LifecycleBadge status={m.contact.lifecycle_status} className="flex-shrink-0" />
                            </div>
                            {flags.length > 0 && (
                              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{flags.join(' · ')}</p>
                            )}
                            <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5">
                              <Fact label="Recruiter" size="xs">{m.assignee?.name || 'Nobody'}</Fact>
                              <Fact label="Tries" size="xs">{m.attempts} of {m.max_attempts}</Fact>
                              <Fact label="Where they are" size="xs" wide>{bucketLabel(m.bucket, m)}</Fact>
                              <Fact label="Last outcome" size="xs">
                                {m.outcome ? OUTCOME_LABELS[m.outcome] : '—'}
                                {m.last_outcome_at && (
                                  <span className="text-gray-400 dark:text-gray-500"> · {relTime(m.last_outcome_at)}</span>
                                )}
                              </Fact>
                              <Fact label="Next try" size="xs">{next ? (Date.parse(next) > Date.now() ? shortWhen(next) : 'Now') : '—'}</Fact>
                              <Fact label="First text" size="xs" wide>{memberTextLabel(m)}</Fact>
                            </dl>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  {isAdmin && isOpenList && (
                    <th className="w-10 px-3 py-2">
                      <input
                        type="checkbox"
                        checked={allOnPage}
                        onChange={() => setSelected(allOnPage ? new Set() : new Set(pageIds))}
                        aria-label="Select everyone on this page"
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                      />
                    </th>
                  )}
                  <th className={thCls}>Name</th>
                  <th className={thCls}>Recruiter</th>
                  <th className={thCls}>Where they are</th>
                  <th className={`${thCls} text-right`}>Tries</th>
                  <th className={thCls}>Last outcome</th>
                  <th className={thCls}>Next try</th>
                  <th className={thCls}>First text</th>
                  <th className={thCls}>Stage</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                {membersLoading && members.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-10 text-center">
                      <Loader2 className="mx-auto h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
                    </td>
                  </tr>
                ) : members.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                      Nobody on this list matches these filters.
                    </td>
                  </tr>
                ) : (
                  members.map((m) => {
                    const next = memberNextTry(m);
                    const flags = memberFlagLabels(m);
                    return (
                      <tr key={m.id} className={`transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50 ${membersLoading ? 'opacity-60' : ''}`}>
                        {isAdmin && isOpenList && (
                          <td className="px-3 py-2.5">
                            {m.state === 'open' && (
                              <input
                                type="checkbox"
                                checked={selected.has(m.id)}
                                onChange={() => setSelected((prev) => {
                                  const n = new Set(prev);
                                  if (n.has(m.id)) n.delete(m.id); else n.add(m.id);
                                  return n;
                                })}
                                aria-label={`Select ${m.contact.name}`}
                                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                              />
                            )}
                          </td>
                        )}
                        <td className="max-w-[14rem] px-3 py-2.5">
                          <RowLink
                            to={`/crm/contacts/${m.contact.id}`}
                            title={m.contact.name}
                            className="block truncate text-sm font-medium text-gray-900 hover:text-primary-700 dark:text-gray-100 dark:hover:text-primary-400"
                          >
                            {m.contact.name || 'Unnamed lead'}
                          </RowLink>
                          {flags.length > 0 && (
                            <span className="block truncate text-xs text-gray-500 dark:text-gray-400" title={flags.join(' · ')}>
                              {flags.join(' · ')}
                            </span>
                          )}
                        </td>
                        <td className={`${tdCls} whitespace-nowrap`}>{m.assignee?.name || 'Nobody'}</td>
                        <td className={`${tdCls} min-w-[10rem]`}>{bucketLabel(m.bucket, m)}</td>
                        <td className={tdNum}>{m.attempts} of {m.max_attempts}</td>
                        <td className={`${tdCls} whitespace-nowrap`}>
                          {m.outcome ? OUTCOME_LABELS[m.outcome] : '—'}
                          {m.last_outcome_at && <span className="block text-xs text-gray-400 dark:text-gray-500">{relTime(m.last_outcome_at)}</span>}
                        </td>
                        <td className={`${tdCls} whitespace-nowrap`}>
                          {next ? (Date.parse(next) > Date.now() ? shortWhen(next) : 'Now') : '—'}
                        </td>
                        <td className={`${tdCls} min-w-[9rem] text-xs`}>{memberTextLabel(m)}</td>
                        <td className="px-3 py-2.5"><LifecycleBadge status={m.contact.lifecycle_status} /></td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">{pageRangeLabel(offset, members.length, total)}</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setOffset((o) => Math.max(0, o - MEMBERS_PAGE_SIZE))}
                disabled={offset === 0 || membersLoading}
                className={btnGhost}
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Previous
              </button>
              <button
                type="button"
                onClick={() => setOffset((o) => o + MEMBERS_PAGE_SIZE)}
                disabled={offset + members.length >= total || membersLoading}
                className={btnGhost}
              >
                Next <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------------- Dialogs */}
      {dialog?.kind === 'move' && (
        <MoveDialog
          count={selected.size}
          options={pickable.map((m) => ({ value: m.id, label: `${m.name}${outTodaySuffix(m)}` }))}
          onClose={() => setDialog(null)}
          onMove={moveSelected}
        />
      )}
      {(dialog?.kind === 'share' || dialog?.kind === 'shareFrom') && (
        <ShareDialog
          title={dialog.kind === 'share' ? 'Share out the people nobody has called yet' : `Share out ${dialog.name}'s people nobody has called yet`}
          explain={
            dialog.kind === 'share'
              ? 'Everyone on this list who hasn’t been called yet is split evenly between the recruiters you pick, and each person’s owner changes to match. People with a reply, a booked call or a try already made stay where they are.'
              : `${dialog.name} is out. The people on their share who haven’t been called yet are split evenly between the recruiters you pick, and each person’s owner changes to match. Anyone ${dialog.name} has already spoken to stays with them.`
          }
          options={pickable
            .filter((m) => dialog.kind !== 'shareFrom' || m.id !== dialog.userId)
            .map((m) => ({ value: m.id, label: `${m.name}${outTodaySuffix(m)}` }))}
          initial={defaultShareRecipients(list.recruiters, dialog.kind === 'shareFrom' ? dialog.userId : null)}
          onClose={() => setDialog(null)}
          onShare={(ids) => (dialog.kind === 'shareFrom' ? shareFrom(dialog.userId, ids) : shareOut(ids))}
        />
      )}
      {dialog?.kind === 'close' && (
        <CloseDialog open={list.counts.open} onClose={() => setDialog(null)} onConfirm={closeList} />
      )}
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/crm/outreach"
      className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 transition-colors duration-150 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
    >
      <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Outreach lists
    </Link>
  );
}

function MoveDialog({
  count,
  options,
  onClose,
  onMove,
}: {
  count: number;
  options: Array<{ value: string; label: string }>;
  onClose: () => void;
  onMove: (userId: string) => void;
}) {
  const [to, setTo] = useState('');
  return (
    <Modal isOpen onClose={onClose} title={`Move ${count === 1 ? '1 person' : `${count.toLocaleString()} people`} to…`} maxWidth="max-w-md">
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          The recruiter you pick works them from now on and becomes their owner, so replies and calls reach them.
        </p>
        <FilterSelect
          label="Recruiter"
          value={to}
          onChange={setTo}
          options={[{ value: '', label: 'Choose a recruiter…' }, ...options]}
        />
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={() => onMove(to)} disabled={!to} className={btnPrimary}>
            <ArrowRightLeft className="h-4 w-4" aria-hidden="true" /> Move
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ShareDialog({
  title,
  explain,
  options,
  initial,
  onClose,
  onShare,
}: {
  title: string;
  explain: string;
  options: Array<{ value: string; label: string }>;
  initial: string[];
  onClose: () => void;
  onShare: (userIds: string[]) => void;
}) {
  const [picked, setPicked] = useState<Set<string>>(() => new Set(initial.filter((u) => options.some((o) => o.value === u))));
  const toggle = (u: string) => setPicked((prev) => {
    const n = new Set(prev);
    if (n.has(u)) n.delete(u); else n.add(u);
    return n;
  });
  return (
    <Modal isOpen onClose={onClose} title={title} maxWidth="max-w-md">
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">{explain}</p>
        <fieldset>
          <legend className="mb-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">Share between</legend>
          <div className="max-h-64 space-y-0.5 overflow-y-auto rounded-lg border border-gray-200 p-1.5 dark:border-gray-700">
            {options.length === 0 && <p className="p-2 text-sm text-gray-500 dark:text-gray-400">No recruiters to choose from.</p>}
            {options.map((o) => (
              <label
                key={o.value}
                className="flex min-h-[44px] cursor-pointer items-center gap-2 rounded-md px-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700/50 sm:min-h-0 sm:py-1.5"
              >
                <input
                  type="checkbox"
                  checked={picked.has(o.value)}
                  onChange={() => toggle(o.value)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                />
                {o.label}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={() => onShare(Array.from(picked))} disabled={picked.size === 0} className={btnPrimary}>
            <Send className="h-4 w-4" aria-hidden="true" /> Share out
          </button>
        </div>
      </div>
    </Modal>
  );
}

function CloseDialog({ open, onClose, onConfirm }: { open: number; onClose: () => void; onConfirm: (archiveUnreached: boolean) => void }) {
  // Default ON (plan §B7): the people nobody reached are the ones who would otherwise flood the
  // inbox the moment the list's flag lifts — and archiving loses nothing, a reply reopens them.
  const [archive, setArchive] = useState(true);
  return (
    <Modal isOpen onClose={onClose} title="Close this list?" maxWidth="max-w-md">
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {open > 0
            ? `${open.toLocaleString()} ${open === 1 ? 'person is' : 'people are'} still open on this list. `
            : ''}
          People we reached go back to the normal lead lists with their recruiter. Any list texts that haven’t gone out are stopped.
        </p>
        <label className="flex cursor-pointer items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
          <input
            type="checkbox"
            checked={archive}
            onChange={(e) => setArchive(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
          />
          <span>
            Archive the people we never reached
            <span className="block text-xs text-gray-500 dark:text-gray-400">They come back automatically if they reply.</span>
          </span>
        </label>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className={btnGhost}>Keep it open</button>
          <button type="button" onClick={() => onConfirm(archive)} className={btnDanger}>Close list</button>
        </div>
      </div>
    </Modal>
  );
}
